import { Modifier } from './modifier';
import BaseAttribute, { AttributeExport } from '../attributes/baseAttribute';
import BaseAttributeCollection from '../attributes/baseAttributeCollection';
import ValueAttribute, { ValueExport } from '../attributes/valueAttribute';
import DiceAttribute from '../attributes/diceAttribute';
import Talent from '../attributes/talent';
import { AttributeFactory } from '../attributes/attributeFactory';
import { Category, UrangstReason } from './allValues';
import { ClassCardData } from '@/interfaces/character/classCard';
import AllCollection from '../attributes/allCollection';

export interface CharacterShortData {
  name: string;
  class: string;
  shortDescription: string | null;
  userId: string;
  characterId: string;
  level: number;
}

export interface CharacterExportData {
  name: string;
  characterId: string | null;
  userId: string | null;
  race: string;
  description: string | null;
  class: string;
  modifiers: Modifier[];
  values: AttributeExport[];
  currentValues?: ValueExport[];
  classcards?: ClassCardData[];
  activeClasscards?: Array<ClassCardData | null>;
  doubts?: Record<UrangstReason, string | null>;
  miscText?: string;
}

export default class Character {
  public name: string | null = null;
  public class: string | null = null;
  public race: string | null = null;
  public description: string | null = null;
  public classcards: ClassCardData[] = [];
  public activeClasscards: Array<ClassCardData | null> = [];
  private characterId: string | null = null;
  private userId: string | null = null;
  private values: BaseAttribute[] = [];
  private currentValues: ValueAttribute[] = [];
  private finalValues: BaseAttribute[] = [];
  private modifiers: Modifier[] = [];
  private doubts: Record<UrangstReason, string | null> = {
    Urangst: null,
    Panik: null,
    Wut: null,
    Irrsinn: null,
    Vergessen: null,
  };
  private miscText: string = '';

  get experience() {
    return this.modifiers
      .map((modifier) => {
        return modifier.attributeAbbreviation === 'ep' ? modifier.value : 0;
      })
      .reduce((accumulator, current) => accumulator + current);
  }

  public constructor(data: CharacterExportData | null = null) {
    if (data !== null) {
      this.import(data, true);
    } else {
      this.init();
    }
  }

  public init() {
    const baseAttributeCollection = new BaseAttributeCollection();
    this.values.push(...baseAttributeCollection.baseResources);
    this.values.push(...baseAttributeCollection.baseExperience);
    this.values.push(...baseAttributeCollection.baseDiceResources);
    this.values.push(...baseAttributeCollection.baseDiceAttributes);
    this.values.push(...baseAttributeCollection.baseElements);
    this.values.push(...baseAttributeCollection.baseCombatDiceAttributes);
    this.currentValues.push(...baseAttributeCollection.baseResources);
    this.currentValues.push(...baseAttributeCollection.baseCombatResources);

    this.calculateFinalValues();
  }

  public export(): CharacterExportData {
    if (this.name === null || this.class === null || this.race === null) {
      throw new Error('Cannot export not initialized character');
    }
    const values = this.values.map((value) => value.export());
    const modifiers = this.modifiers;
    const currentValues = this.currentValues.map((value) => value.export() as ValueExport);
    return {
      name: this.name,
      characterId: this.characterId,
      userId: null,
      class: this.class,
      race: this.race,
      description: this.description,
      currentValues,
      modifiers,
      values,
      miscText: this.miscText,
      doubts: this.doubts,
    };
  }

  public getCharacterId() {
    return this.characterId;
  }

  public getCurrentResources() {
    return this.currentValues.filter((attribute) => attribute.category === Category.Resource);
  }

  public setCurrentResource(abbreviation: string, value: number) {
    const index = this.currentValues.findIndex((currValue) => currValue.abbreviation === abbreviation);
    if (index < 0) {
      throw new Error('Currvalue to set not found: ' + abbreviation);
    }
    this.currentValues[index].value = value;
  }

  public exportCharacterShort(): CharacterShortData {
    if (this.name === null || this.class === null || this.race === null) {
      throw new Error('Cannot export not initialized character');
    }
    if (this.characterId === null) {
      throw new Error('Cannot export character without id');
    }
    if (this.userId === null) {
      throw new Error('Cannot export character without userId');
    }
    return {
      name: this.name,
      class: this.class,
      characterId: this.characterId,
      level: this.getHeroLevel(),
      userId: this.userId,
      shortDescription: !this.description ? null : this.description,
    };
  }

  /**
   * Imports a character based of the exported data
   * @param data Characterdata that was previously exported with export()
   * @param clear true to clear all existing values
   */
  public import(data: CharacterExportData, clear = true) {
    const collection = new BaseAttributeCollection();
    if (clear) {
      this.values = [];
    }
    this.name = data.name;
    this.class = data.class;
    this.race = data.race;
    this.description = data.description;
    this.userId = data.userId;
    this.characterId = data.characterId;

    if (typeof data.activeClasscards !== 'undefined') {
      this.activeClasscards = data.activeClasscards;
    }

    if (typeof data.classcards !== 'undefined') {
      this.classcards = data.classcards;
    }

    const baseAttributeCollection = new BaseAttributeCollection();
    this.currentValues.push(...baseAttributeCollection.baseResources);
    this.currentValues.push(...baseAttributeCollection.baseCombatResources);

    if (typeof data.currentValues !== 'undefined') {
      data.currentValues.forEach((value) => {
        // Find index and set value
        const index = this.currentValues.findIndex((localValue) => localValue.abbreviation === value.abbreviation);

        if (index < 0) {
          throw new Error('Could not find currentValue ' + value.abbreviation);
        }

        this.currentValues[index].value = value.value;
      });
    }

    // Init base values
    this.values.push(...collection.baseCombatDiceAttributes);

    data.values.forEach((value) => {
      const attribute = AttributeFactory.import(value);
      this.addAttribute(attribute);
    });

    this.modifiers = data.modifiers;
    const allCollection = new AllCollection();

    // In case we have modifiers without values, we need to create a value for it
    this.modifiers.forEach((modifier) => {
      const index = this.values.findIndex((value) => value.abbreviation === modifier.attributeAbbreviation);
      if (index < 0) {
        const attributeClone = allCollection.getAttributeByAbbreviation(modifier.attributeAbbreviation);
        this.addAttribute(attributeClone);
      }
    });

    if (typeof data.doubts !== 'undefined') {
      this.doubts = data.doubts;
    }

    if (typeof data.miscText !== 'undefined') {
      this.miscText = data.miscText;
    }

    this.calculateFinalValues();
  }

  /**
   * Calculates the hero level based on the Experience
   */
  public getHeroLevel(): number {
    return Math.ceil((Math.sqrt(2500 + 200 * this.experience) - 50) / 100);
  }

  /**
   *
   * @param abbreviation The abbreviation of the value to find
   */
  public getValueByAbbreviation(abbreviation: string): BaseAttribute {
    const value = this.findValueByAbbreviation(abbreviation);

    return value;
  }

  public getBaseValueByAbbreviation(abbreviation: string): BaseAttribute {
    const value = this.findValueByAbbreviation(abbreviation);

    return this.calcFinalValueByAbbreviation(value.abbreviation, true);
  }

  public hasAttribute(abbreviation: string) {
    try {
      this.findValueByAbbreviation(abbreviation);
      return true;
    } catch (error) {
      return false;
    }
  }

  public addAttribute(attribute: BaseAttribute) {
    // prevent duplicates
    const index = this.values.findIndex((value) => value.abbreviation === attribute.abbreviation);
    if (index < 0) {
      this.values.push(attribute);
    } else {
      this.values[index] = attribute;
    }

    this.calculateFinalValues();
  }

  /**
   * Gets the doubt for a passed reason.
   * @param reason The reason for which to return the doubt. If it doesn't exist in doubts object, an error is thrown.
   */
  public getDoubtByReason(reason: UrangstReason) {
    if (typeof this.doubts[reason] === 'undefined') {
      throw new Error('Unknown reason for doubts: ' + reason);
    }

    return this.doubts[reason];
  }

  public getCharacterText() {
    return this.miscText;
  }

  public addNumberModifier(attributeAbbreviation: string, attributeValue: string, value: number, source: string) {
    // check if this attribute exists
    if (this.values.findIndex((findValue) => findValue.abbreviation === attributeAbbreviation) === -1) {
      throw new Error('Attribute ' + attributeAbbreviation + ' not found for adding modifier');
    }
    // Make sure value really is a number
    if (typeof value !== 'number') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        throw new Error('Could not convert value to number: ' + numValue);
      }
      value = numValue;
    }
    this.modifiers.push({ attributeAbbreviation, attributeValue, value, source });

    this.calculateFinalValues();
  }

  public addDiceModifier(attributeAbbreviation: string, dice: number, modifier: number, source: string) {
    // check if this attribute exists
    if (this.values.findIndex((findValue) => findValue.abbreviation === attributeAbbreviation) === -1) {
      throw new Error('Attribute ' + attributeAbbreviation + ' not found for adding modifier');
    }
    // Make sure dice really is a number
    if (typeof dice !== 'number') {
      const numDice = parseInt(dice, 10);
      if (isNaN(numDice)) {
        throw new Error('Could not convert dice to number: ' + numDice);
      }
      dice = numDice;
    }
    // Make sure modifier really is a number
    if (typeof modifier !== 'number') {
      const numModifier = parseInt(modifier, 10);
      if (isNaN(numModifier)) {
        throw new Error('Could not convert dice to number: ' + numModifier);
      }
      modifier = numModifier;
    }
    this.modifiers.push({ attributeAbbreviation, attributeValue: 'numDice', value: dice, source });
    this.modifiers.push({ attributeAbbreviation, attributeValue: 'numBonus', value: modifier, source });

    this.calculateFinalValues();
  }

  public removeAttributeByAbbreviation(abbreviation: string) {
    const index = this.values.findIndex((value) => value.abbreviation === abbreviation);

    if (index < 0) {
      throw new Error('Did not find value with abbreviation ' + abbreviation);
    }
    this.values.splice(index, 1);

    // Remove also any modifier that might exist
    this.modifiers = this.modifiers.filter((modifier) => modifier.attributeAbbreviation !== abbreviation);

    this.calculateFinalValues();
  }

  public setValueByAbbreviation(abbreviation: string, value: number) {
    const foundValueRaw: BaseAttribute = this.findValueByAbbreviation(abbreviation);

    if (!(foundValueRaw instanceof ValueAttribute)) {
      throw new Error('Found value for "' + abbreviation + '" is no ValueAttribute');
    }

    const foundValue: ValueAttribute = foundValueRaw as ValueAttribute;
    foundValue.value = value;
  }

  public setDiceValueByAbbreviation(abbreviation: string, dices: number, modifier: number) {
    const foundValueRaw: BaseAttribute = this.findValueByAbbreviation(abbreviation);

    if (!(foundValueRaw instanceof DiceAttribute)) {
      throw new Error('Found value for "' + abbreviation + '" is no ValueAttribute');
    }

    const foundValue: DiceAttribute = foundValueRaw as DiceAttribute;
    foundValue.numDice = dices;
    foundValue.numBonus = modifier;
  }

  public getTotalModifierValueByType(type: string, valueType: string, onlyBase: boolean): number {
    let totalValue: number = 0;
    this.modifiers
      .filter((modifier) => modifier.attributeAbbreviation === type && modifier.attributeValue === valueType)
      .forEach((modifier) => {
        if (onlyBase && modifier.source !== 'start') {
          return;
        }
        totalValue += modifier.value;
      });

    return totalValue;
  }

  public getModifiers() {
    return this.modifiers;
  }

  public getModifiersOfAttribute(abbrevation: string) {
    return this.modifiers.filter((modifier) => modifier.attributeAbbreviation === abbrevation);
  }

  public getValuesByCategory(category: string) {
    return this.values.filter((value) => value.category === category);
  }

  public getTalentsByTalentField(talentField: string) {
    return this.finalValues.filter((value) => {
      if (value instanceof Talent) {
        return value.talentField === talentField;
      }
      return false;
    });
  }

  public getFinalValueByAbbreviation(abbreviation: string, errorNotFound = true) {
    if (this.finalValues.length === 0) {
      this.calculateFinalValues();
    }
    const foundValues = this.finalValues.filter((finalValue) => finalValue.abbreviation === abbreviation);

    if (foundValues.length === 0) {
      if (errorNotFound) {
        throw new Error('getFinalValueByAbbreviation: final value not found ' + abbreviation);
      } else {
        return new AllCollection().getAttributeByAbbreviation(abbreviation);
      }
    }

    if (foundValues.length > 1) {
      throw new Error('getFinalValueByAbbreviation: more than one value found for ' + abbreviation);
    }

    return foundValues[0];
  }

  public getModifiersBySource(source: string) {
    return this.modifiers.filter((modifier) => modifier.source === source);
  }

  public getModifiersExcludingSource(source: string) {
    return this.modifiers.filter((modifier) => modifier.source !== source);
  }

  private calculateFinalValues() {
    // Reset values
    this.finalValues = [];

    this.values.forEach((value) => {
      const finalValue = this.calcFinalValueByAbbreviation(value.abbreviation);
      this.finalValues.push(finalValue);
    });
  }

  private calcFinalValueByAbbreviation(abbreviation: string, onlyBase = false): ValueAttribute | DiceAttribute {
    // It is necessary to create a fresh attribute so that vue doesn't update the value endlessly
    const value: BaseAttribute = this.findValueByAbbreviation(abbreviation);

    const clone = value.getClone();
    if (clone instanceof ValueAttribute) {
      const modifierValue = this.getTotalModifierValueByType(abbreviation, 'value', onlyBase);
      clone.value += modifierValue;
      return clone as ValueAttribute;
    } else if (clone instanceof DiceAttribute) {
      const modifierValue = this.getTotalModifierValueByType(abbreviation, 'numBonus', onlyBase);
      clone.numBonus += modifierValue;
      const diceValue = this.getTotalModifierValueByType(abbreviation, 'numDice', onlyBase);
      clone.numDice += diceValue;
      return clone as DiceAttribute;
    } else {
      throw new Error('Cannot calc final value for ' + abbreviation);
    }
  }

  /**
   * Searches the attribute based on the abbrevation. Throws an error if not found
   * @param abbreviation The abbreviation of the value to find
   */
  private findValueByAbbreviation(abbreviation: string): BaseAttribute {
    const matchingValue = this.values.filter((localValue) => localValue.abbreviation === abbreviation);

    if (matchingValue.length === 0) {
      throw new Error('Value not found ' + abbreviation);
    }

    if (matchingValue.length !== 1) {
      throw new Error('More than one value found for ' + abbreviation);
    }

    return matchingValue[0];
  }
}
